import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import { Alert } from '@mds/shared/ui/feedback/alert';
import { ImageGallery } from '@mds/shared/ui/image/image-gallery';

import EventCard from '@/components/card/event-card';
import IconCard from '@/components/card/icon-card';
import { MediaCardRow } from '@/components/card/media-card';
import RegistrationCard from '@/components/card/registration-card';
import PageHeader from '@/components/heading/page-header';
import SectionHeader from '@/components/heading/section-header';
import RuledList from '@/components/section/ruled-list';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { eventQueries } from '@/features/events/events.queries';
import { formatSeatsLeft } from '@/lib/format';
import NotFoundPage from './not-found';

const band = 'mx-auto max-w-page px-gutter pt-section';

/** At or under this many seats, the page warns that it is nearly full. */
const FEW_SEATS = 4;

export default function EventDetailPage() {
  const { slug = '' } = useParams();
  const detail = useQuery(eventQueries.detail(slug));
  // Three other open events, soonest first. One spare is asked for so the
  // current event can be left out and three still remain.
  const alsoComing = useQuery({
    ...eventQueries.list({ limit: 4 }),
    select: (response) => response.data.filter((other) => other.slug !== slug).slice(0, 3),
  });

  // A slug nobody has is a wrong URL, not an empty state — the 404 is the
  // honest answer and it keeps the wrong address out of the index.
  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return <NotFoundPage />;
  }

  if (detail.isPending || detail.isError) {
    return (
      <section className={band}>
        {detail.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock
            title="We couldn't load this event"
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
      </section>
    );
  }

  const event = detail.data;
  const { registrationStatus, seatsLeft } = event;
  // The API sends plain text; a blank line in it is a paragraph break.
  // A blank or whitespace-only description leaves the section out.
  const about = event.longDescription
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  // Exactly what the API sends. It already starts with the cover, so
  // `coverImage` is not read here: adding it would show the cover twice.
  const gallery = event.gallery;
  // Each section shows only when the backend sent something to put in it: a
  // missing list, or lines with no text, leave the section out entirely.
  const agenda = (event.agenda ?? []).filter((item) => item.description?.trim());
  const goodToKnow = (event.goodToKnow ?? []).filter(
    (item) => item.label?.trim() && item.value?.trim(),
  );

  return (
    <>
      <section className={band}>
        <PageHeader
          back={{ label: 'Events', to: '/events' }}
          title={event.title}
          description={event.shortDescription}
          // No `meta` row: the date, time, place and price are all on the
          // registration card beside the gallery, so a line of them here only
          // said the same things twice.
        />
      </section>

      {/* The pictures and the way to register, side by side from `lg`, so the
          action is in view as soon as the page opens. Stacked below that, card
          under the gallery.

          3 : 2 in the gallery's favour, so the card is always the narrower
          of the two. The thumbnails run in a row under the large picture
          (`feature-below`) rather than a column beside it.

          Stretched, not top-aligned, so the two end on one line: the row
          takes the taller of them, the large picture fills that height, and
          the card pushes its button to the bottom. */}
      <section className="mx-auto flex max-w-page flex-col gap-block px-gutter pt-block lg:flex-row">
        {/* An event with no pictures has no gallery, and the card takes the
            row alone. */}
        {gallery.length > 0 && (
          <ImageGallery
            label={event.title}
            layout="feature-below"
            images={gallery.map((src) => ({ src, alt: event.title }))}
            className="min-w-0 lg:flex-3"
          />
        )}

        {/* Never wider than the gallery's large frame: side by side it shares
            the row, and stacked under the gallery it would otherwise run the
            full page width while the picture above it stops at its cap. */}
        <div className="flex w-full max-w-gallery-feature min-w-0 flex-col lg:flex-2">
          <RegistrationCard event={event} className="flex-1" />
        </div>
      </section>

      <section className={band}>
        {/* First in the band, above the description: a closed door or the
            last few seats is the one thing here that changes what the visitor
            does next. */}
        {registrationStatus === 'closed' ? (
          <Alert tone="neutral" title="Registration has closed" className="mb-block max-w-prose">
            This event has already started.
          </Alert>
        ) : (
          // A full event needs no alert: its card's button leads to the waitlist.
          registrationStatus === 'open' &&
          seatsLeft <= FEW_SEATS && (
            <Alert
              tone="warning"
              title={formatSeatsLeft(seatsLeft)}
              className="mb-block max-w-prose"
            >
              {/* Only a confirmed registration takes a seat: a free one is
                  confirmed at once, a paid one when it is paid in full. */}
              {event.isFree
                ? 'Your seat is held as soon as you register.'
                : 'A seat is only yours once it is paid in full at the front desk.'}
            </Alert>
          )
        )}

        {about.length > 0 && (
          <>
            <SectionHeader as="h2" title="About this event" />
            <div className="mt-5 flex max-w-prose flex-col gap-5">
              {about.map((paragraph) => (
                <p key={paragraph} className="text-body-lg text-pretty text-ink-secondary">
                  {paragraph}
                </p>
              ))}
            </div>
          </>
        )}

        {agenda.length > 0 && (
          <>
            <SectionHeader
              as="h2"
              title="Agenda"
              // Spaced from the description above it, when there is one.
              className={about.length > 0 ? 'mt-block' : undefined}
            />
            {/* RuledList's two-column mode is the agenda: a mono time marker and
                a sentence. An untimed line keeps its place with a dash. */}
            <RuledList
              className="mt-6"
              accent="brand"
              items={agenda.map((item) => ({
                marker: item.time ?? '—',
                description: item.description,
              }))}
            />
          </>
        )}
      </section>

      {/* The same row the space page's booking rules draw: a ruled `IconCard`
          per fact, mint label over a sentence. Three across from `lg`. */}
      {goodToKnow.length > 0 && (
        <section className={band}>
          <SectionHeader as="h2" title="Good to know" />
          <div className="mt-6 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            {goodToKnow.map((item) => (
              <IconCard
                key={item.label}
                ruled
                accent="brand"
                eyebrow={item.label}
                description={item.value}
              />
            ))}
          </div>
        </section>
      )}

      {/* Left out until there is something else on: a heading over nothing
          reads as a page that failed to load. */}
      {alsoComing.data && alsoComing.data.length > 0 && (
        <section className="mx-auto max-w-page px-gutter pt-section">
          <SectionHeader as="h2" title="Also coming up" />
          <MediaCardRow className="mt-block">
            {alsoComing.data.map((other) => (
              <EventCard key={other.id} event={other} />
            ))}
          </MediaCardRow>
        </section>
      )}
    </>
  );
}
